'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Alert, Box, type SelectChangeEvent } from '@mui/material';
import VtkViewer, { type VtkViewerHandle } from '../components/VtkViewer';
import ControlPanel, { type ViewFieldKey } from '../components/controlPanel';
import InfoPanel from '../components/InfoPanel';
import type { VtkDatasetInfo, VtkPolylineSet } from '../types/vtk';
import { STENOSIS_CAMERA, BIFURCATION_CAMERA } from '../lib/createVtkScene';

// Attach the stored JWT (set at login) so the backend's auth dependency accepts the request.
function authHeader(): HeadersInit {
  const token =
    typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

interface BackendSurfaceResponse {
  message: string;
  mesh: VtkDatasetInfo;
  activeScalar?: string;
}

interface BackendStreamlineResponse {
  message: string;
  streamlines: VtkPolylineSet | null;
  colorScalar?: string | null;
}

interface RomInfoResponse {
  message: string;
  inlets: string[];
  info: Record<
    string,
    {
      f_range: [number, number];
      time_index_range: [number, number];
      time_values: number[];
    }
  >;
  defaults: {
    inlet: string;
    f: number;
    time_index: number;
  };
}

const VIZ_CACHE_MAX = 25;

type VizSnapshot = {
  dataset: VtkDatasetInfo | null;
  streamlines: VtkPolylineSet | null;
  activeScalar: string | null;
  streamlineScalar: string | null;
};

function vizKey(
  inlet: string,
  f: number,
  timeIndex: number,
  field: ViewFieldKey,
) {
  return `${inlet}|${f}|${timeIndex}|${field}`;
}

function parseVizKey(key: string): {
  inlet: string;
  f: number;
  timeIndex: number;
  field: ViewFieldKey;
} | null {
  const pipe = key.indexOf('|');
  if (pipe < 0) return null;
  const pipe2 = key.indexOf('|', pipe + 1);
  if (pipe2 < 0) return null;
  const pipe3 = key.indexOf('|', pipe2 + 1);
  if (pipe3 < 0) return null;

  const inlet = key.slice(0, pipe);
  const f = Number(key.slice(pipe + 1, pipe2));
  const timeIndex = Number(key.slice(pipe2 + 1, pipe3));
  const field = key.slice(pipe3 + 1) as ViewFieldKey;

  if (!inlet || Number.isNaN(f) || Number.isNaN(timeIndex) || !field) {
    return null;
  }
  return { inlet, f, timeIndex, field };
}

const FIELD_CONFIG: Record<
  ViewFieldKey,
  {
    mode: 'scalar' | 'streamlines';
    backendField?: 'p' | 'wss';
    activeScalarLabel: string;
  }
> = {
  pressure: {
    mode: 'scalar',
    backendField: 'p',
    activeScalarLabel: 'Pressure (Pa)',
  },
  wss: {
    mode: 'scalar',
    backendField: 'wss',
    activeScalarLabel: 'Wall Shear Stress (Pa)',
  },
  // STREAMLINES DISABLED - see FIELD_OPTIONS in controlPanel.tsx.
  // velocity: {
  //   mode: 'streamlines',
  //   activeScalarLabel: 'Velocity Magnitude (m/s)',
  // },
};

export default function Page() {
  const router = useRouter();

  // TODO(local-auth-bypass): token validation and login redirect.
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      router.replace('/login');
      return;
    }
  }, [router]);

  // TODO(local-auth-bypass): 401 handling and token cleanup.
  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem('access_token');
    document.cookie = 'access_token=; path=/; max-age=0';
    router.replace('/login');
  }, [router]);

  const [inlets, setInlets] = useState<string[]>([]);
  const [fRange, setFRange] = useState<[number, number]>([0.5, 1.5]);


  const [inlet, setInlet] = useState<string>('');
  const [fValue, setFValue] = useState<number>(1.0);
  const [timeIndex, setTimeIndex] = useState<number>(25);
  const [selectedField, setSelectedField] = useState<ViewFieldKey>('wss');

  const [dataset, setDataset] = useState<VtkDatasetInfo | null>(null);
  const [streamlines, setStreamlines] = useState<VtkPolylineSet | null>(null);
  const [activeScalar, setActiveScalar] = useState<string | null>(null);
  const [streamlineScalar, setStreamlineScalar] = useState<string | null>(null);

  const [loadingMesh, setLoadingMesh] = useState(false);
  const [loadingStreamlines, setLoadingStreamlines] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [infoOpen, setInfoOpen] = useState(true);

  const vtkViewerRef = useRef<VtkViewerHandle>(null);

  // LRU cache for up to 25 visualization snapshots
  const vizCacheRef = useRef(new Map<string, VizSnapshot>());
  const vizHistoryRef = useRef<string[]>([]);
  const vizHistoryIdxRef = useRef(-1);

  const canGoBack = vizHistoryIdxRef.current > 0;
  const canGoForward =
    vizHistoryIdxRef.current < vizHistoryRef.current.length - 1;

  const applySnapshot = useCallback((snap: VizSnapshot) => {
    setDataset(snap.dataset);
    setStreamlines(snap.streamlines);
    setActiveScalar(snap.activeScalar);
    setStreamlineScalar(snap.streamlineScalar);
  }, []);

  const navigateToIndex = useCallback(
    async (idx: number) => {
      const history = vizHistoryRef.current;
      if (idx < 0 || idx >= history.length) return;
      const key = history[idx];
      const snap = vizCacheRef.current.get(key);
      if (!snap) return;

      const params = parseVizKey(key);
      if (!params) return;

      vizHistoryIdxRef.current = idx;
      applySnapshot(snap);

      // Restore all control-panel state
      setFValue(params.f);
      setTimeIndex(params.timeIndex);
      setSelectedField(params.field);
      setInlet(params.inlet);

      // Re-fetch ROM info when inlet changes so fRange is correct
      if (params.inlet !== inlet) {
        try {
          const apiUrl = process.env.NEXT_PUBLIC_API_URL ?? '';
          const res = await fetch(`${apiUrl}/rom/info`, {
            headers: authHeader(),
          });
          if (res.ok) {
            const data: RomInfoResponse = await res.json();
            const inletInfo = data.info?.[params.inlet];
            if (inletInfo) {
              setFRange(inletInfo.f_range);
            }
          }
        } catch { /* sliders already restored, non-critical */ }
      }

      setNavTick((t) => t + 1);
    },
    [applySnapshot, inlet],
  );

  const handlePrevious = useCallback(() => {
    void navigateToIndex(vizHistoryIdxRef.current - 1);
  }, [navigateToIndex]);

  const handleNext = useCallback(() => {
    void navigateToIndex(vizHistoryIdxRef.current + 1);
  }, [navigateToIndex]);

  const [navTick, setNavTick] = useState(0);

  const recordVizHistory = useCallback(
    (key: string) => {
      const history = vizHistoryRef.current;
      const currentIdx = vizHistoryIdxRef.current;

      if (currentIdx >= 0 && history[currentIdx] === key) return;

      const newHistory = history.slice(0, currentIdx + 1);
      newHistory.push(key);
      vizHistoryRef.current = newHistory;
      vizHistoryIdxRef.current = newHistory.length - 1;
      setNavTick((t) => t + 1);
    },
    [],
  );

  const config = FIELD_CONFIG[selectedField];
  const showStreamlines = config.mode === 'streamlines';
  const meshVisible = config.mode === 'scalar';
  const isBusy = loadingMesh || (showStreamlines ? loadingStreamlines : false);

  const loadRomInfo = async () => {
    const apiUrl = process.env.NEXT_PUBLIC_API_URL;
    if (!apiUrl) throw new Error('NEXT_PUBLIC_API_URL is not defined');

    const response = await fetch(`${apiUrl}/rom/info`, {
      headers: authHeader(),
    });

    if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);

    const data: RomInfoResponse = await response.json();

    const availableInlets = data.inlets ?? [];
    setInlets(availableInlets);

    const defaultInlet = data.defaults?.inlet ?? availableInlets[0] ?? 'LMCA';
    const defaultF = data.defaults?.f ?? 1.0;
    const defaultTimeIndex = 25;

    setInlet(defaultInlet);
    setFValue(defaultF);
    setTimeIndex(defaultTimeIndex);

    const inletInfo = data.info?.[defaultInlet];
    if (inletInfo) {
      setFRange(inletInfo.f_range);
    }

    return { defaultInlet, defaultF, defaultTimeIndex };
  };

  const loadMesh = async (
    inletParam?: string,
    fParam?: number,
    timeParam?: number,
    fieldParam?: ViewFieldKey,
  ) => {
    const currentInlet = inletParam ?? inlet;
    const currentF = fParam ?? fValue;
    const currentTime = timeParam ?? timeIndex;
    const currentField = fieldParam ?? selectedField;
    const currentConfig = FIELD_CONFIG[currentField];

    if (currentConfig.mode !== 'scalar' || !currentConfig.backendField) {
      setDataset(null);
      setActiveScalar(null);
      setLoadingMesh(false);
      return;
    }

    setLoadingMesh(true);
    setError(null);

    const key = vizKey(currentInlet, currentF, currentTime, currentField);

    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL;
      if (!apiUrl) throw new Error('NEXT_PUBLIC_API_URL is not defined');

      const params = new URLSearchParams({
        inlet: currentInlet,
        f: String(currentF),
        time_index: String(currentTime),
        field: currentConfig.backendField,
      });

      const response = await fetch(`${apiUrl}/mesh/surface?${params.toString()}`, {
        headers: authHeader(),
      });

      // TODO(local-auth-bypass): 401 handling and token cleanup.
      if (response.status === 401) {
        handleUnauthorized();
        return;
      }

      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);

      const jsonData: BackendSurfaceResponse = await response.json();
      const scalar = jsonData.activeScalar ?? null;
      setDataset(jsonData.mesh);
      setActiveScalar(scalar);

      // Update or create cache entry
      const existing = vizCacheRef.current.get(key);
      if (existing) {
        existing.dataset = jsonData.mesh;
        existing.activeScalar = scalar;
      } else {
        if (vizCacheRef.current.size >= VIZ_CACHE_MAX) {
          const oldest = vizCacheRef.current.keys().next().value;
          if (oldest) vizCacheRef.current.delete(oldest);
        }
        vizCacheRef.current.set(key, {
          dataset: jsonData.mesh,
          activeScalar: scalar,
          streamlines: null,
          streamlineScalar: null,
        });
      }
      recordVizHistory(key);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setDataset(null);
      setActiveScalar(null);
    } finally {
      setLoadingMesh(false);
    }
  };

  const loadStreamlines = async (
    inletParam?: string,
    fParam?: number,
    timeParam?: number,
    fieldParam?: ViewFieldKey,
  ) => {
    const currentField = fieldParam ?? selectedField;
    const currentConfig = FIELD_CONFIG[currentField];

    if (currentConfig.mode !== 'streamlines') {
      setStreamlines(null);
      setStreamlineScalar(null);
      setLoadingStreamlines(false);
      return;
    }

    const currentInlet = inletParam ?? inlet;
    const currentF = fParam ?? fValue;
    const currentTime = timeParam ?? timeIndex;

    setLoadingStreamlines(true);
    setError(null);

    const key = vizKey(currentInlet, currentF, currentTime, currentField);

    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL;
      if (!apiUrl) throw new Error('NEXT_PUBLIC_API_URL is not defined');

      const params = new URLSearchParams({
        inlet: currentInlet,
        f: String(currentF),
        time_index: String(currentTime),
      });

      const response = await fetch(`${apiUrl}/mesh/streamlines?${params.toString()}`, {
        headers: authHeader(),
      });

      // TODO(local-auth-bypass): 401 handling and token cleanup.
      if (response.status === 401) {
        handleUnauthorized();
        return;
      }

      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);

      const jsonData: BackendStreamlineResponse = await response.json();
      const scalar = jsonData.colorScalar ?? null;
      setStreamlines(jsonData.streamlines);
      setStreamlineScalar(scalar);

      // Update or create cache entry
      const existing = vizCacheRef.current.get(key);
      if (existing) {
        existing.streamlines = jsonData.streamlines;
        existing.streamlineScalar = scalar;
      } else {
        if (vizCacheRef.current.size >= VIZ_CACHE_MAX) {
          const oldest = vizCacheRef.current.keys().next().value;
          if (oldest) vizCacheRef.current.delete(oldest);
        }
        vizCacheRef.current.set(key, {
          dataset: null,
          activeScalar: null,
          streamlines: jsonData.streamlines,
          streamlineScalar: scalar,
        });
      }
      recordVizHistory(key);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setStreamlines(null);
      setStreamlineScalar(null);
    } finally {
      setLoadingStreamlines(false);
    }
  };

  useEffect(() => {
    void (async () => {
      try {
        const defaults = await loadRomInfo();
        if (!defaults) return;
        const { defaultInlet, defaultF, defaultTimeIndex } = defaults;
        await Promise.all([
          loadMesh(defaultInlet, defaultF, defaultTimeIndex, selectedField),
          loadStreamlines(defaultInlet, defaultF, defaultTimeIndex, selectedField),
        ]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInletChange = async (event: SelectChangeEvent<string>) => {
    const nextInlet = event.target.value;
    setInlet(nextInlet);

    const apiUrl = process.env.NEXT_PUBLIC_API_URL ?? '';

    try {
      const res = await fetch(`${apiUrl}/rom/info`, {
        headers: authHeader(),
      });
      if (res.ok) {
        const data: RomInfoResponse = await res.json();
        const inletInfo = data.info?.[nextInlet];
        if (inletInfo) {
          setFRange(inletInfo.f_range);
          const clampedF = Math.min(Math.max(fValue, inletInfo.f_range[0]), inletInfo.f_range[1]);
          const clampedTime = Math.min(timeIndex, inletInfo.time_values.length - 1);
          setFValue(clampedF);
          setTimeIndex(clampedTime);
          return;
        }
      }
    } catch { /* fall through */ }
  };

  const handleFChange = (_: Event, value: number | number[]) => {
    if (typeof value !== 'number') return;
    setFValue(value);
  };

  const handleTimeIndexChange = (_: Event, value: number | number[]) => {
    if (typeof value !== 'number') return;
    setTimeIndex(value);
  };

  const handleFieldChange = (event: SelectChangeEvent<ViewFieldKey>) => {
    const nextField = event.target.value as ViewFieldKey;
    setSelectedField(nextField);
  };

  const handleSimulate = async () => {
    await Promise.all([
      loadMesh(inlet, fValue, timeIndex, selectedField),
      loadStreamlines(inlet, fValue, timeIndex, selectedField),
    ]);
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
      {/* Full-screen VTK viewer */}
      <Box sx={{ position: 'absolute', inset: 0 }}>
        {error && (
          <Alert severity='error' sx={{ position: 'absolute', top: 12, left: 12, right: 12, zIndex: 20 }}>
            {error}
          </Alert>
        )}
        <VtkViewer
          ref={vtkViewerRef}
          dataset={dataset}
          activeScalar={activeScalar}
          activeScalarLabel={config.activeScalarLabel}
          streamlines={streamlines}
          streamlineScalar={streamlineScalar}
          streamlineScalarLabel={config.activeScalarLabel}
          showStreamlines={showStreamlines}
          meshVisible={meshVisible}
          status={isBusy ? 'computing' : 'ready'}
          computingStreamlines={loadingStreamlines}
        />
      </Box>

      <InfoPanel
        open={infoOpen}
        onToggle={() => setInfoOpen((o) => !o)}
      />

      {/* Floating control toolbar — bottom-center */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 30,
          maxWidth: '105vw',
          width: 'auto',
        }}
      >
        <ControlPanel
          inlets={inlets}
          inlet={inlet}
          fValue={fValue}
          fRange={fRange}
          timeIndex={timeIndex}
          field={selectedField}
          onInletChange={handleInletChange}
          onFChange={handleFChange}
          onTimeIndexChange={handleTimeIndexChange}
          onFieldChange={handleFieldChange}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onSimulate={handleSimulate}
          canGoBack={canGoBack}
          canGoForward={canGoForward}
          onZoomStenosis={() => vtkViewerRef.current?.setCameraState(STENOSIS_CAMERA)}
          onZoomBifurcation={() =>
            vtkViewerRef.current?.setCameraState(BIFURCATION_CAMERA)
          }
          hideOverlay={!infoOpen}
        />
      </Box>
    </Box>
  );
}
